<template>
  <slot
    name="content"
    :submitForm="submitForm"
    :formValuesHandler="formValuesHandler"
    :errors="errors"
    :submitting="isSubmitting"
    :showModal="showModal"
  />
  <Basemodal :action-required="true" injectionName="flightUpgradeSearchModal">
    <template #modal-content="{ closeModal }">
      <div class="base-model__content">
        <div class="base-model__header base-model__header--border">
          <p class="base-model__header-title heading-h5">{{ modalTitle }}</p>
        </div>
        <div class="base-model__body base-model__body--padded">
          <div class="rte">
            <p v-html="modalText"></p>
          </div>
        </div>
        <div class="base-model__footer base-model__footer--fullwidth">
          <button
            class="button button--primary"
            :onClick="
              () => {
                closeModal();
              }
            "
          >
            Okay
          </button>
        </div>
      </div>
    </template>
  </Basemodal>
</template>

<script setup>
import { ref, onMounted, provide, watch } from 'vue';
import { useQuery } from '@tanstack/vue-query';
import optiClient from '../../services/api';
import Basemodal from '../BaseModal/BaseModal.vue';
const pnr = ref('');
const lastName = ref('');
const isEconomyMaxRef = ref(false);
const errors = ref({});
const formValues = ref({});
const isSubmitting = ref(false);
const showModal = ref(false);
const modalText = ref('');
const modalTitle = ref('');
let submissionCount = 0;
const randomId = Math.random().toString(36).substring(2, 12);

const { data, isLoading, fetchStatus, error, refetch } = useQuery(
  [
    'flightUpgradeSearch',
    pnr.value,
    lastName.value,
    isEconomyMaxRef.value,
    submissionCount,
    randomId,
  ],
  async () => {
    const result = await optiClient.flightUpgrade.getApiV1PlusGradeSearch(
      pnr.value,
      lastName.value,
      isEconomyMaxRef.value,
    ); 
    return result;
  },
  {
    refetchOnWindowFocus: false,
    enabled: false,
    fetchPolicy: 'no-cache',
  },
);

provide('flightUpgradeSearchModal', showModal);

watch(fetchStatus, () => {
  if (fetchStatus.value === 'idle' && data?.value?.data) {
    isSubmitting.value = false;
    const redirectUrl = data.value.data?.redirectUrl
      ? data.value.data.redirectUrl
      : null;
    const responseStatus = data.value.data?.responseStatus
      ? data.value.data.responseStatus
      : null;
    if (redirectUrl) {
      modalTitle.value = 'Able to upgrade';
      modalText.value = 'Please hold on while we redirect you.';
      showModal.value = true;
      window.location.href = redirectUrl;
    } else {
      modalTitle.value = 'Unable to upgrade';
      switch (responseStatus) {
        case 'PNR_NOT_FOUND':
          modalText.value =
            'We could not verify the details entered. Please check them and try again, or contact our Support for assistance.';
          break;
        case 'NOT_ELIGIBLE':
          modalText.value =
            'We are sorry, we are unable to upgrade your seat at this time. <br/> We have no space available.';
          break;
        default:
          modalText.value =
            'We are sorry, we are unable to upgrade your seat at this time.';
          break;
      }
      showModal.value = true;
    }
  }
});

watch(error, () => {
  isSubmitting.value = false;
  modalTitle.value = 'Unable to upgrade';
  modalText.value =
    'We are sorry, we are unable to upgrade your seat at this time.';
  showModal.value = true;
});

const props = defineProps({
  fieldNames: {
    type: String,
    default: '',
  },
  isEconomyMax: {
    type: Boolean,
    default: false
  }
}); 

const submitForm = async () => {
  showModal.value = false;
  const names = Object.keys(formValues.value);
  const regex = /^[a-zA-Z0-9]{6}$/;
  names.forEach((name) => {
    if (name === 'reference-number' && !regex.test(formValues.value[name])) {
      errors.value[name] =
        'Please enter a valid Booking Reservation number';
      return;
    }
 
    if (formValues.value[name] === '') {
      errors.value[name] = 'Please enter a valid value';
      return;
    }
    delete errors.value[name];
  });

  if (Object.keys(errors.value).length !== 0) {
    return;
  }

  pnr.value = formValues.value['reference-number'];
  lastName.value = formValues.value['lastname'];
  isEconomyMaxRef.value = props.isEconomyMax
  isSubmitting.value = true;
  submissionCount++;

  try {
    await refetch(); // Trigger the query again
  } catch (error) {
    // Handle error
    isSubmitting.value = false;
    modalTitle.value = 'Unable to upgrade';
    modalText.value =
      'We are sorry, we are unable to upgrade your seat at this time.';
    showModal.value = true;
  }
};

const formValuesHandler = (value, name) => {
  formValues.value[name] = value;
  errors.value[name] = '';
};

onMounted(() => {
  const fieldNames = props.fieldNames.split(',');
  const names = {};
  fieldNames.forEach((entry) => {
    names[entry] = '';
  });
  formValues.value = { ...names };
  errors.value = { ...names };
});
</script>
